import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PropertyCard } from "@/components/PropertyCard";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { Loader2, MapPin, Plus, CheckCircle2, Clock, ShieldCheck, Building2, Crown, Upload, X, Star } from "lucide-react";
import { PROPERTY_TYPES, CITIES, FACING_OPTIONS, APPROVAL_OPTIONS } from "@/lib/format";
import { loadRazorpay } from "@/lib/razorpay";

const pinIcon = L.divIcon({ className: "", html: '<div class="price-pin">📍 Drop here</div>', iconAnchor: [40, 14] });

function Picker({ pos, setPos }) {
  useMapEvents({ click(e) { setPos([e.latlng.lat, e.latlng.lng]); } });
  return pos ? <Marker position={pos} icon={pinIcon} /> : null;
}

const AMENITIES = ["Gym", "Swimming Pool", "24/7 Security", "Power Backup", "Car Parking", "Lift", "Clubhouse", "Kids Play Area", "Garden", "Gated Community"];

export default function ListProperty() {
  const { user, siteConfig } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    title: "", description: "", property_type: "apartment", listing_type: "sale",
    price: "", area: "", area_unit: "sqft", bedrooms: "", bathrooms: "",
    city: "Hyderabad", locality: "", address: "", images: "", amenities: [], survey_number: "", extent: "", facing: "", approval: "",
    poster_role: "owner", district: "", mandal: "", village: "", road_access: "", rera_number: "", contact_name: "", contact_phone: "", video: "",
  });

  const cityObj = CITIES.find((c) => c.name === f.city) || CITIES[0];

  const { data: mine = [], refetch } = useQuery({
    queryKey: ["my-properties"],
    queryFn: async () => (await api.get("/my/properties")).data,
    enabled: !!user,
  });

  const { data: pricing } = useQuery({ queryKey: ["pricing"], queryFn: async () => (await api.get("/settings")).data.pricing });

  const promote = async (pid, tier) => {
    try {
      const { data: order } = await api.post("/payments/promote/create-order", { property_id: pid, tier });
      if (order.demo) {
        await api.post("/payments/promote/verify", { order_id: order.order_id, payment_id: "demo", signature: "demo" });
        toast.success("Listing promoted!"); refetch();
      } else {
        await loadRazorpay();
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: "INR", order_id: order.order_id,
          name: "Dotlands Realty", description: `Promote: ${tier}`,
          handler: async (res) => { await api.post("/payments/promote/verify", { order_id: res.razorpay_order_id, payment_id: res.razorpay_payment_id, signature: res.razorpay_signature }); toast.success("Listing promoted!"); refetch(); },
          theme: { color: "#0E3B2E" },
        });
        rzp.open();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const limit = siteConfig?.free_ad_limit || 10;
  const isFreeUser = user && user.role !== "admin" && !user.is_premium;
  const isDeveloper = user && (user.role === "admin" || user.plan === "developer");
  const reachedLimit = isFreeUser && mine.length >= limit;

  const toggleAmenity = (a) => setF((s) => ({ ...s, amenities: s.amenities.includes(a) ? s.amenities.filter((x) => x !== a) : [...s.amenities, a] }));

  const bulkImport = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return toast.error("Paste at least one listing line");
    const items = [];
    for (const line of lines) {
      const parts = line.split(",").map((s) => s.trim());
      if (parts.length < 6) continue;
      const [title, ptype, price, area, unit, cityName, locality] = parts;
      const c = CITIES.find((x) => x.name.toLowerCase() === (cityName || "").toLowerCase()) || cityObj;
      items.push({
        title, description: "", property_type: ptype || "plot", listing_type: "sale",
        price: Number(price) || 0, area: Number(area) || 0, area_unit: unit || "sqyd",
        bedrooms: 0, bathrooms: 0, city: c.name, locality: locality || "", address: "",
        lat: c.lat, lng: c.lng, images: [], amenities: [],
      });
    }
    if (items.length === 0) return toast.error("No valid lines. Format: Title, type, price, area, unit, city, locality");
    setBulkSaving(true);
    try {
      const { data } = await api.post("/properties/bulk", { items });
      toast.success(`${data.count} listings submitted for verification`);
      setBulkText("");
      refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkSaving(false); }
  };

  const requestSold = async (pid) => {
    try { const { data } = await api.post(`/properties/${pid}/request-sold`); toast.success(data.message); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const boostListing = async (pid) => {
    try {
      const { data: order } = await api.post("/payments/boost/create-order", { property_id: pid });
      if (order.demo) {
        await api.post("/payments/boost/verify", { order_id: order.order_id, payment_id: "demo", signature: "demo" });
        toast.success("Listing boosted to the top!"); refetch();
      } else {
        await loadRazorpay();
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: "INR", order_id: order.order_id,
          name: "Dotlands.in", description: "Boost to Top",
          handler: async (res) => { await api.post("/payments/boost/verify", { order_id: res.razorpay_order_id, payment_id: res.razorpay_payment_id, signature: res.razorpay_signature }); toast.success("Listing boosted!"); refetch(); },
          theme: { color: "#0E3B2E" },
        });
        rzp.open();
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        urls.push(`${API}/files/${data.path}`);
      }
      setImages((s) => [...s, ...urls]);
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.title || !f.price || !f.area) return toast.error("Fill title, price and area");
    if (reachedLimit) return toast.error(`Free plan allows up to ${limit} ads. Upgrade to Premium for unlimited.`);
    const [lat, lng] = pos || [cityObj.lat, cityObj.lng];
    setSaving(true);
    try {
      await api.post("/properties", {
        ...f,
        price: Number(f.price), area: Number(f.area),
        bedrooms: Number(f.bedrooms) || 0, bathrooms: Number(f.bathrooms) || 0,
        lat, lng,
        images,
      });
      toast.success(user.role === "admin" ? "Property published!" : "Submitted! Pending Dotlands approval.");
      setF({ ...f, title: "", description: "", price: "", area: "", bedrooms: "", bathrooms: "", locality: "", address: "", images: "", amenities: [], survey_number: "", extent: "", facing: "", approval: "", district: "", mandal: "", village: "", road_access: "", rera_number: "", contact_name: "", contact_phone: "", video: "" });
      setPos(null);
      setImages([]);
      refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8">
      <Seo
        title="List Your Property Free — Sell Agricultural Land & Plots | Dotlands Realty"
        description="Post your agricultural land, farm land, plot, home or commercial property for sale in Telangana. Free listings, verified by Dotlands Realty, reaching genuine buyers near Hyderabad."
        path="/list-property"
      />
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">List your property</h1>
      <p className="text-slate-500 mt-1">Post for free. All buyer enquiries are handled by the Dotlands Realty desk.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-2.5 text-sm" data-testid="verify-banner">
          <ShieldCheck className="w-4 h-4 shrink-0" /> New listings go live only after Dotlands verification.
        </div>
        {isFreeUser && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-2.5 text-sm" data-testid="free-usage">
            <Building2 className="w-4 h-4 shrink-0" /> Free plan: {mine.length}/{limit} ads used
          </div>
        )}
        {isDeveloper && (
          <div className="flex items-center gap-2 bg-slate-900 text-white rounded-xl px-4 py-2.5 text-sm">
            <Crown className="w-4 h-4 shrink-0 text-amber-400" /> Developer: unlimited + auto-featured
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
          <div>
            <Label className="mb-1.5 block">I am posting as</Label>
            <Select value={f.poster_role} onValueChange={(v) => setF({ ...f, poster_role: v })}>
              <SelectTrigger data-testid="lp-poster-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="builder">Builder / Developer</SelectItem>
                <SelectItem value="land_developer">Land Developer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">Title *</Label>
            <Input data-testid="lp-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. 3BHK Luxury Apartment in Gachibowli" />
          </div>
          <div>
            <Label className="mb-1.5 block">Property type</Label>
            <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
              <SelectTrigger data-testid="lp-property-type"><SelectValue /></SelectTrigger>
              <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label className="mb-1.5 block">Price (₹) *</Label>
              <Input data-testid="lp-price" inputMode="numeric" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value.replace(/\D/g, "") })} placeholder="12500000" /></div>
            <div><Label className="mb-1.5 block">Area *</Label>
              <Input data-testid="lp-area" inputMode="numeric" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value.replace(/\D/g, "") })} placeholder="1850" /></div>
            <div><Label className="mb-1.5 block">Unit</Label>
              <Select value={f.area_unit} onValueChange={(v) => setF({ ...f, area_unit: v })}>
                <SelectTrigger data-testid="lp-area-unit"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sqft">sq.ft</SelectItem><SelectItem value="sqyd">sq.yd</SelectItem><SelectItem value="acre">acre</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">Bedrooms</Label>
              <Input data-testid="lp-bedrooms" inputMode="numeric" value={f.bedrooms} onChange={(e) => setF({ ...f, bedrooms: e.target.value.replace(/\D/g, "") })} placeholder="3" /></div>
            <div><Label className="mb-1.5 block">Bathrooms</Label>
              <Input data-testid="lp-bathrooms" inputMode="numeric" value={f.bathrooms} onChange={(e) => setF({ ...f, bathrooms: e.target.value.replace(/\D/g, "") })} placeholder="3" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">City</Label>
              <Select value={f.city} onValueChange={(v) => { setF({ ...f, city: v }); setPos(null); }}>
                <SelectTrigger data-testid="lp-city"><SelectValue /></SelectTrigger>
                <SelectContent>{CITIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Locality</Label>
              <Input data-testid="lp-locality" value={f.locality} onChange={(e) => setF({ ...f, locality: e.target.value })} placeholder="Gachibowli" /></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label className="mb-1.5 block">District</Label>
              <Input data-testid="lp-district" value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} placeholder="Ranga Reddy" /></div>
            <div><Label className="mb-1.5 block">Mandal</Label>
              <Input data-testid="lp-mandal" value={f.mandal} onChange={(e) => setF({ ...f, mandal: e.target.value })} placeholder="Kadthal" /></div>
            <div><Label className="mb-1.5 block">Village</Label>
              <Input data-testid="lp-village" value={f.village} onChange={(e) => setF({ ...f, village: e.target.value })} placeholder="Village / project" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">Road access</Label>
              <Input data-testid="lp-road" value={f.road_access} onChange={(e) => setF({ ...f, road_access: e.target.value })} placeholder="e.g. 40ft BT road" /></div>
            <div><Label className="mb-1.5 block">RERA number (if any)</Label>
              <Input data-testid="lp-rera" value={f.rera_number} onChange={(e) => setF({ ...f, rera_number: e.target.value })} placeholder="RERA reg. no." /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">Contact name</Label>
              <Input data-testid="lp-contact-name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} placeholder="Your name" /></div>
            <div><Label className="mb-1.5 block">Contact phone</Label>
              <Input data-testid="lp-contact-phone" inputMode="numeric" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value.replace(/\D/g, "") })} placeholder="10-digit mobile" /></div>
          </div>
          <div><Label className="mb-1.5 block">Video link (YouTube / walkthrough)</Label>
            <Input data-testid="lp-video" value={f.video} onChange={(e) => setF({ ...f, video: e.target.value })} placeholder="https://youtu.be/..." /></div>
          {(f.property_type === "agricultural" || f.property_type === "plot") && (
            <div><Label className="mb-1.5 block">Approval type</Label>
              <Select value={f.approval} onValueChange={(v) => setF({ ...f, approval: v })}>
                <SelectTrigger data-testid="lp-approval"><SelectValue placeholder="Select approval" /></SelectTrigger>
                <SelectContent>{APPROVAL_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.property_type === "agricultural" && (
            <>
              <div><Label className="mb-1.5 block">Survey Number</Label>
                <Input data-testid="lp-survey" value={f.survey_number} onChange={(e) => setF({ ...f, survey_number: e.target.value })} placeholder="e.g. Sy. No. 123/A" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="mb-1.5 block">Extent</Label>
                  <Input data-testid="lp-extent" value={f.extent} onChange={(e) => setF({ ...f, extent: e.target.value })} placeholder="e.g. 2 Acres 20 Guntas" /></div>
                <div><Label className="mb-1.5 block">Facing</Label>
                  <Select value={f.facing} onValueChange={(v) => setF({ ...f, facing: v })}>
                    <SelectTrigger data-testid="lp-facing"><SelectValue placeholder="Select facing" /></SelectTrigger>
                    <SelectContent>{FACING_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
          <div><Label className="mb-1.5 block">Description</Label>
            <Textarea data-testid="lp-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} placeholder="Describe your property..." /></div>
          <div><Label className="mb-1.5 block">Property photos</Label>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl py-6 cursor-pointer hover:border-emerald-400" data-testid="lp-upload-label">
              <input type="file" accept="image/*" multiple className="hidden" data-testid="lp-file-input"
                onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)} />
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-600" /> : <Upload className="w-6 h-6 text-slate-400" />}
              <span className="text-sm text-slate-500 mt-1">{uploading ? "Uploading..." : "Click to upload land / property photos"}</span>
            </label>
            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {images.map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} alt="" className="w-full h-16 object-cover rounded-lg border" />
                    <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))} data-testid={`lp-remove-img-${i}`}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="mb-2 block">Amenities</Label>
            <div className="flex flex-wrap gap-2">
              {AMENITIES.map((a) => (
                <button key={a} type="button" onClick={() => toggleAmenity(a)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.amenities.includes(a) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-emerald-600" /> Pin exact location (click on map)</Label>
            <div className="h-64 rounded-xl overflow-hidden border border-slate-200">
              <MapContainer center={[cityObj.lat, cityObj.lng]} zoom={12} style={{ height: "100%", width: "100%" }} key={f.city}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Picker pos={pos} setPos={setPos} />
              </MapContainer>
            </div>
            <p className="text-xs text-slate-400 mt-1">{pos ? `Pinned at ${pos[0].toFixed(4)}, ${pos[1].toFixed(4)}` : "Using city center — click map to set exact spot"}</p>
          </div>

          {reachedLimit ? (
            <div className="text-center bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="lp-limit-reached">
              <p className="text-sm text-amber-800 mb-3">You've used all {limit} free ads. Upgrade to Premium for unlimited listings.</p>
              <Button onClick={() => navigate("/premium")} data-testid="lp-upgrade" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
              </Button>
            </div>
          ) : (
            <Button data-testid="lp-submit" onClick={submit} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-5 h-5 mr-2" /> Post Property</>}
            </Button>
          )}
        </div>

        <div className="lg:col-span-1">
          {isDeveloper && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="bulk-import-card">
              <div className="flex items-center gap-2 mb-2">
                <Upload className="w-5 h-5 text-emerald-600" />
                <h2 className="font-heading font-bold text-lg text-slate-900">Bulk import</h2>
                <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-slate-900 text-white">Developer</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">One listing per line:<br />Title, type, price, area, unit, city, locality</p>
              <Textarea data-testid="bulk-text" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={5}
                placeholder={"Sunrise Plots, plot, 4500000, 250, sqyd, Hyderabad, Kollur\nGreen Acres Farm, agricultural, 12000000, 4, acre, Hyderabad, Chevella"}
                className="text-xs font-mono" />
              <Button data-testid="bulk-submit" onClick={bulkImport} disabled={bulkSaving} className="w-full mt-3 bg-slate-900 hover:bg-slate-800">
                {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Import listings</>}
              </Button>
            </div>
          )}
          <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">My listings ({mine.length})</h2>
          {mine.length === 0 ? (
            <div className="text-sm text-slate-400 border border-dashed rounded-xl p-6 text-center">No listings yet.</div>
          ) : (
            <div className="space-y-4">
              {mine.map((p) => (
                <div key={p.id} className="relative">
                  <PropertyCard p={p} />
                  <span className={`absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${p.status === "sold" ? "bg-red-600 text-white" : p.status === "approved" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
                    {p.status === "sold" ? "Sold Out" : p.status === "approved" ? <><CheckCircle2 className="w-3 h-3" /> Live</> : <><Clock className="w-3 h-3" /> Pending</>}
                  </span>
                  {p.status === "approved" && (
                    p.sold_requested
                      ? <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Sold approval pending with Dotlands</div>
                      : <Button size="sm" variant="outline" className="w-full mt-2" data-testid={`request-sold-${p.id}`} onClick={() => requestSold(p.id)}>Mark as Sold</Button>
                  )}
                  {p.status === "approved" && !p.sold_requested && (
                    p.featured
                      ? <div className="mt-2 text-xs text-[#A8863A] font-semibold flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#C8A24C] text-[#C8A24C]" /> Boosted / Featured</div>
                      : <Button size="sm" className="w-full mt-2 bg-[#C8A24C] hover:bg-[#A8863A] text-[#0E3B2E] font-bold" data-testid={`boost-${p.id}`} onClick={() => boostListing(p.id)}>Boost to Top · ₹150 / 1 week</Button>
                  )}
                  {p.status === "approved" && !p.sold_requested && !p.featured && pricing && (
                    <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid={`promote-${p.id}`}>
                      <Button size="sm" variant="outline" className="text-[11px] px-1" onClick={() => promote(p.id, "featured")}>Featured ₹{pricing.featured}</Button>
                      <Button size="sm" variant="outline" className="text-[11px] px-1" onClick={() => promote(p.id, "premium")}>Premium ₹{pricing.premium}</Button>
                      <Button size="sm" variant="outline" className="text-[11px] px-1" onClick={() => promote(p.id, "super_premium")}>Super ₹{pricing.super_premium}</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
